/**
 * Notifications Resources
 *
 * Provides access to @classic-homes/notifications package documentation.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { ResourceTemplate } from '@modelcontextprotocol/sdk/server/mcp.js';
import notificationsCatalog from '../data/notifications-catalog.json' with { type: 'json' };
import type { NotificationsCatalog } from '../types.js';

const catalog = notificationsCatalog as unknown as NotificationsCatalog;

/**
 * Register notifications-related resources
 */
export function registerNotificationsResources(server: McpServer): void {
  // Full notifications catalog
  server.resource('Notifications Package', 'notifications://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog, null, 2),
      },
    ],
  }));

  // Core subpackage
  server.resource('Notifications Core', 'notifications://core', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.core.importPath,
            description: catalog.subpackages.core.description,
            modules: catalog.subpackages.core.modules,
            types: catalog.subpackages.core.types,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Svelte subpackage
  server.resource('Notifications Svelte', 'notifications://svelte', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.svelte.importPath,
            description: catalog.subpackages.svelte.description,
            modules: catalog.subpackages.svelte.modules,
            storeState: catalog.subpackages.svelte.storeState,
            storeMethods: catalog.subpackages.svelte.storeMethods,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Types only
  server.resource('Notifications Types', 'notifications://types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'TypeScript types exported from @classic-homes/notifications/core',
            types: catalog.subpackages.core.types,
          },
          null,
          2
        ),
      },
    ],
  }));

  // API operations only
  server.resource('Notifications API', 'notifications://api', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'Notifications API client methods from @classic-homes/notifications/core',
            api: catalog.subpackages.core.modules.api,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Utilities only
  server.resource('Notifications Utils', 'notifications://utils', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'Utility functions from @classic-homes/notifications/core',
            utils: catalog.subpackages.core.modules.utils,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Individual module access via template
  server.resource(
    'Notifications Module',
    new ResourceTemplate('notifications://core/{module}', { list: undefined }),
    async (uri, params) => {
      const moduleName = params.module as string;
      const modules = catalog.subpackages.core.modules;
      const module = modules[moduleName as keyof typeof modules];

      if (!module) {
        const availableModules = Object.keys(modules);
        return {
          contents: [
            {
              uri: uri.href,
              mimeType: 'application/json',
              text: JSON.stringify({
                error: `Module "${moduleName}" not found`,
                availableModules,
              }),
            },
          ],
        };
      }

      return {
        contents: [
          {
            uri: uri.href,
            mimeType: 'application/json',
            text: JSON.stringify(module, null, 2),
          },
        ],
      };
    }
  );

  // Quick reference for common operations
  server.resource(
    'Notifications Quick Reference',
    'notifications://quick-reference',
    async (uri) => ({
      contents: [
        {
          uri: uri.href,
          mimeType: 'application/json',
          text: JSON.stringify(
            {
              description: 'Quick reference for common notification operations',
              fetchNotifications: {
                code: "import { notificationStore } from '@classic-homes/notifications/svelte';\n\nawait notificationStore.fetch({ page: 1, limit: 20 });",
                description: 'Fetch notifications with pagination',
              },
              svelteStore: {
                code: "import { notifications, unreadCount, isLoadingNotifications } from '@classic-homes/notifications/svelte';\n\n// In component\n{#if $isLoadingNotifications}\n  <Spinner />\n{:else}\n  <p>You have {$unreadCount} unread</p>\n  {#each $notifications as n}\n    <p>{n.title}</p>\n  {/each}\n{/if}",
                description: 'Use reactive Svelte stores for notification state',
              },
              markAsRead: {
                code: "import { notificationStore } from '@classic-homes/notifications/svelte';\n\nawait notificationStore.markRead('notification-id');\nawait notificationStore.markManyRead(['id1', 'id2']);\nawait notificationStore.markAllRead();",
                description: 'Mark notifications as read',
              },
              directApi: {
                code: "import { notificationsApi } from '@classic-homes/notifications/core';\n\nconst response = await notificationsApi.list({ page: 1, limit: 20 });\nawait notificationsApi.markRead('id');\nawait notificationsApi.dismiss('id');",
                description: 'Direct API access without Svelte store',
              },
              utilities: {
                code: "import {\n  getNotificationIconName,\n  getNotificationIconColor,\n  getPriorityBadgeVariant,\n  formatRelativeTime,\n} from '@classic-homes/notifications/core';\n\ngetNotificationIconName('security'); // 'shield'\ngetNotificationIconColor('security'); // 'text-red-500'\ngetPriorityBadgeVariant('urgent'); // 'destructive'\nformatRelativeTime('2024-01-22T10:00:00Z'); // '2h ago'",
                description: 'Utility functions for display',
              },
              preferences: {
                code: "import { notificationService } from '@classic-homes/notifications/core';\n\nconst { preferences } = await notificationService.getPreferences();\nawait notificationService.updatePreference({\n  channelId: 'email',\n  notificationType: 'security',\n  enabled: true,\n  frequency: 'immediate',\n});",
                description: 'Manage notification preferences',
              },
            },
            null,
            2
          ),
        },
      ],
    })
  );
}
